/**
 * Hint: the one tooltip. React Aria's Tooltip needs a focusable
 * trigger. A control (a Button) is its own trigger; anything else --
 * a chip, a swatch, a citation -- is wrapped in a Focusable span that
 * wears the button role React Aria requires of a Focusable child
 * (a presentation role is refused, 2026-10-02).
 */
import type { CSSProperties, ReactNode } from 'react';
import { Focusable, OverlayArrow, Tooltip, TooltipTrigger } from 'react-aria-components';
import { CONTROL, FRAME, RADIUS, TEXT, Z, usePalette } from '../theme';

/** The degrees the arrow turns to point from a bubble at its placement. */
function arrowRotation(placement: 'top' | 'bottom' | 'left' | 'right'): number {
  switch (placement) {
    case 'top':
      return 0;
    case 'bottom':
      return 180;
    case 'left':
      return -90;
    case 'right':
      return 90;
  }
}

export function Hint({
  title,
  children,
  placement = 'top',
  control = false,
}: {
  title: ReactNode;
  children: ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  /** The child is already a focusable control; do not wrap it. */
  control?: boolean;
}) {
  const p = usePalette();
  const bubble = {
    zIndex: Z.popover,
    maxWidth: FRAME.popoverMax,
    padding: `${CONTROL.padYCompact}px ${CONTROL.padXCompact}px`,
    borderRadius: RADIUS.md,
    background: p.inkStrong,
    color: p.surface,
    fontSize: TEXT.caption,
    lineHeight: 1.4,
    boxShadow: `0 8px 24px -8px ${p.shadow}`,
    wordBreak: 'break-word',
  } satisfies CSSProperties;
  const rotate = arrowRotation(placement);
  return (
    <TooltipTrigger delay={300}>
      {control ? (
        children
      ) : (
        <Focusable>
          <span role="button" style={{ display: 'inline-flex', borderRadius: RADIUS.sm }}>
            {children}
          </span>
        </Focusable>
      )}
      <Tooltip placement={placement} offset={6} style={bubble}>
        <OverlayArrow>
          <svg
            width={8}
            height={8}
            viewBox="0 0 8 8"
            aria-hidden="true"
            style={{ display: 'block', fill: p.inkStrong, transform: `rotate(${rotate}deg)` }}
          >
            <path d="M0 0 L4 4 L8 0" />
          </svg>
        </OverlayArrow>
        {title}
      </Tooltip>
    </TooltipTrigger>
  );
}
